import React, { useEffect, useState } from 'react';
import { Sparkles, Truck, Gift, Check, Copy } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';
import './TopPromoBar.css';

const DEFAULT_PROMOS = [
  { text: "FREE SHIPPING ON ORDERS OVER RS. 2,500", type: "shipping", icon: Truck },
  { text: "DISCOVER THE LUXURY ARCHIVES - EST. 2023", type: "brand", icon: Gift }
];

const TopPromoBar = () => {
  const [promos, setPromos] = useState(DEFAULT_PROMOS);
  const [copiedCode, setCopiedCode] = useState(null);

  // Fetch live coupons to display in the promo bar
  useEffect(() => {
    const fetchCoupons = async () => {
      const adminToken = localStorage.getItem('adminToken');
      const userToken = localStorage.getItem('token');
      const token = adminToken || userToken;

      const headers = {
        'Content-Type': 'application/json',
      };
      if (token && token !== 'simulated-token') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const endpointsToTry = [
        `${BASE_URL}Coupon/all`,
        `${BASE_URL}Coupon/getAll`,
        `${BASE_URL}Coupon/getAllCoupons`,
        `${BASE_URL}Coupon/active`,
      ];

      for (const url of endpointsToTry) {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) continue;

          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data.data) ? data.data
            : Array.isArray(data.coupons) ? data.coupons
            : Array.isArray(data.result) ? data.result
            : [];

          const activeCoupons = list.filter(c => {
            if (c.isActive === false) return false;

            // Check if coupon has expired
            const expiryDateStr = c.expiryDate || c.expiry || c.validUntil || c.endDate;
            if (expiryDateStr) {
              const expiryDate = new Date(expiryDateStr);
              const now = new Date();
              if (!isNaN(expiryDate.getTime()) && expiryDate < now) {
                return false; // Expired
              }
            }
            return true;
          });

          if (activeCoupons.length > 0) {
            const couponPromos = activeCoupons.map(coupon => {
              const code = coupon.code || coupon.couponCode || coupon.name || 'CODE';
              const expiryDateStr = coupon.expiryDate || coupon.expiry || coupon.validUntil || coupon.endDate;
              return {
                text: `${coupon.description || 'Special Discount'} · Use Code:`,
                code: code,
                type: "coupon",
                icon: Gift,
                expiryDate: expiryDateStr ? new Date(expiryDateStr) : null
              };
            });

            setPromos([
              ...couponPromos,
              { text: "FREE SHIPPING ON ALL ORDERS OVER RS. 2,500", type: "shipping", icon: Truck },
            ]);
            break;
          }
        } catch (err) {
          // ignore error
        }
      }
    };

    fetchCoupons();

    const handleUserUpdate = () => {
      fetchCoupons();
    };
    window.addEventListener('userUpdated', handleUserUpdate);
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  // Periodically check for expired coupons and remove them
  useEffect(() => {
    const timer = setInterval(() => {
      setPromos(prev => {
        let hasChanges = false;
        const now = new Date();
        const valid = prev.filter(p => {
          if (p.expiryDate && !isNaN(p.expiryDate) && p.expiryDate <= now) {
            hasChanges = true;
            return false;
          }
          return true;
        });

        if (hasChanges) {
          const stillHasCoupon = valid.some(p => p.type === 'coupon');
          if (!stillHasCoupon) {
            return DEFAULT_PROMOS; // Revert to default text if no coupons remain
          }
          return valid;
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCopy = (e, code) => {
    e.stopPropagation();
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }).catch(() => {});
  };

  const handlePromoAction = (promo) => {
    // Action handling removed to enforce checkout-only login
  };

  if (promos.length === 0) return null;

  const renderPromoItems = () => {
    return promos.map((promo, idx) => {
      const PromoIcon = promo.icon || Sparkles;
      const isThisCopied = copiedCode === promo.code;
      return (
        <React.Fragment key={idx}>
          <div 
            className={`marquee-item promo-type-${promo.type}`}
            onClick={() => handlePromoAction(promo)}
            style={{ cursor: promo.action === 'login' ? 'pointer' : 'default' }}
          >
            <span className="promo-icon-wrap">
              <PromoIcon size={12} className="promo-pulse-icon" />
            </span>

            <span className="promo-text-content">
              {promo.text}
            </span>

            {promo.code && (
              <div className="promo-code-container" onClick={(e) => handleCopy(e, promo.code)}>
                <span className="promo-code-tag">{promo.code}</span>
                <button className="promo-copy-icon-btn" title="Copy Code">
                  {isThisCopied ? <Check size={11} className="copied-green" /> : <Copy size={11} />}
                </button>
                {isThisCopied && <span className="promo-copy-tooltip">Copied!</span>}
              </div>
            )}
          </div>
          <span className="marquee-separator">✦</span>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="top-promo-bar">
      <div className="promo-container marquee-mode">
        <div className="marquee-track">
          <div className="marquee-group">
            {renderPromoItems()}
          </div>
          <div className="marquee-group" aria-hidden="true">
            {renderPromoItems()}
          </div>
          <div className="marquee-group" aria-hidden="true">
            {renderPromoItems()}
          </div>
        </div>
      </div>
      
      {/* Sleek golden thin border line below */}
      <div className="promo-border-glow" />
    </div>
  );
};

export default TopPromoBar;
